import { useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  Plug,
  KeyRound,
  ShieldCheck,
  Wrench,
  ArrowLeft,
  Check,
  Copy,
  ExternalLink,
} from 'lucide-react'

const MCP_URL = (import.meta.env.VITE_MCP_URL as string) || 'https://mcp.heytasky.com/mcp'

function Copyable({ value, label = 'Copy' }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(value).catch(() => {})
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1500)
      }}
      className="btn-subtle absolute right-2 top-2 z-10 border border-line bg-surface px-2.5 py-1.5 text-xs"
      aria-label={label}
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5 text-accent-green" /> Copied
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" /> {label}
        </>
      )}
    </button>
  )
}

function Code({ children, copy }: { children: string; copy?: boolean }) {
  return (
    <div className="relative max-w-full overflow-hidden rounded-xl border border-line bg-panel">
      {copy !== false && <Copyable value={children} />}
      <pre className="overflow-x-auto p-4 pr-24 font-mono text-[12.5px] leading-relaxed text-ink-700">
        <code>{children}</code>
      </pre>
    </div>
  )
}

function Card({ children }: { children: ReactNode }) {
  return <div className="card min-w-0 p-4 sm:p-5">{children}</div>
}

function Heading({
  icon,
  title,
  subtitle,
}: {
  icon: ReactNode
  title: string
  subtitle?: string
}) {
  return (
    <div className="mb-3 flex items-start gap-2.5">
      <span className="mt-0.5 text-brand-500">{icon}</span>
      <div>
        <h2 className="font-display text-base font-semibold tracking-tight text-ink-900">{title}</h2>
        {subtitle && <p className="mt-0.5 text-sm text-ink-400">{subtitle}</p>}
      </div>
    </div>
  )
}

type Client = {
  name: string
  kind: 'cli' | 'json' | 'connector'
  blurb: string
  steps: string[]
  oauth: string
  key: string
}

const CLIENTS: Client[] = [
  {
    name: 'Claude Code',
    kind: 'cli',
    blurb: 'One command in your terminal. Claude opens a browser to sign you in.',
    steps: [
      'Run the command below in any project.',
      'A browser opens: sign in to Hey Tasky and approve access.',
      'Ask Claude to "list my channels" to confirm it is connected.',
    ],
    oauth: `claude mcp add --transport http heytasky ${MCP_URL}`,
    key: `claude mcp add --transport http heytasky ${MCP_URL} --header "Authorization: Bearer YOUR_API_KEY"`,
  },
  {
    name: 'ChatGPT',
    kind: 'connector',
    blurb: 'ChatGPT adds Hey Tasky as a custom MCP plugin, on the web only. Full access, including posting, is on Business, Enterprise, and Edu. Plus and Pro may be read-only or need Developer mode under Settings, Security. Free is not supported.',
    steps: [
      'Go to chatgpt.com/plugins, click the plus button, then "Add custom MCP server" (some accounts show "Create MCP App").',
      `Name it Hey Tasky and paste the server URL: ${MCP_URL}`,
      'Choose OAuth (ChatGPT has no custom headers, so API keys do not work). Tick "I understand and want to continue", then "Create as a plugin".',
      'Sign in to Hey Tasky and approve. In any chat, type @ and pick Hey Tasky.',
    ],
    oauth: MCP_URL,
    key: MCP_URL,
  },
  {
    name: 'Cursor',
    kind: 'json',
    blurb: 'Add the server to your MCP config. Cursor runs the sign-in flow on first use.',
    steps: [
      'Open Cursor Settings, then MCP, then "Add new MCP server" (this edits mcp.json).',
      'Paste the JSON below.',
      'Cursor prompts you to sign in to Hey Tasky and approve.',
    ],
    oauth: `{
  "mcpServers": {
    "heytasky": {
      "url": "${MCP_URL}"
    }
  }
}`,
    key: `{
  "mcpServers": {
    "heytasky": {
      "url": "${MCP_URL}",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}`,
  },
  {
    name: 'VS Code',
    kind: 'json',
    blurb: 'Add the server to your MCP config (Copilot agent mode or any MCP extension).',
    steps: [
      'Open your MCP config (mcp.json) and add the entry below.',
      'Reload, then sign in to Hey Tasky when prompted.',
    ],
    oauth: `{
  "servers": {
    "heytasky": {
      "url": "${MCP_URL}"
    }
  }
}`,
    key: `{
  "servers": {
    "heytasky": {
      "url": "${MCP_URL}",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}`,
  },
  {
    name: 'Codex',
    kind: 'json',
    blurb: 'Add the server to your Codex MCP config.',
    steps: [
      'Add the entry below to your Codex MCP config.',
      'Sign in to Hey Tasky on first use.',
    ],
    oauth: `{
  "mcpServers": {
    "heytasky": {
      "url": "${MCP_URL}"
    }
  }
}`,
    key: `{
  "mcpServers": {
    "heytasky": {
      "url": "${MCP_URL}",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}`,
  },
]

const TOOLS: { name: string; desc: string }[] = [
  { name: 'list_channels', desc: 'See the social accounts connected to your workspace.' },
  { name: 'list_social_sets', desc: 'See your social sets, so you can say "post this to my text list".' },
  { name: 'create_post', desc: 'Draft a post for one or more channels.' },
  { name: 'schedule_post', desc: 'Queue a post to publish at a specific time.' },
  { name: 'get_queue', desc: 'See what is scheduled and what already went out.' },
  { name: 'get_post_results', desc: 'Read real performance metrics for published posts.' },
  { name: 'list_replies', desc: 'Read classified replies: leads, questions, praise.' },
  { name: 'get_best_times', desc: 'Find your best-performing publish slots.' },
]

export function McpDocsPage() {
  const [client, setClient] = useState<Client>(CLIENTS[0])
  const [mode, setMode] = useState<'OAuth' | 'API key'>('OAuth')
  const snippet = mode === 'OAuth' ? client.oauth : client.key

  return (
    <div className="min-h-screen overflow-x-hidden bg-paper">
      <header className="border-b border-line bg-panel px-4 py-4 sm:px-6">
        <div className="mx-auto flex max-w-3xl min-w-0 items-center justify-between gap-3">
          <span className="font-display text-lg font-bold tracking-tight text-ink-900">
            Hey Tasky
          </span>
          <Link
            to="/settings"
            className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900"
          >
            <ArrowLeft className="h-4 w-4" /> Back to app
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl min-w-0 space-y-4 px-4 py-8 sm:px-6">
        <div>
          <h1 className="max-w-full break-words font-display text-2xl font-bold tracking-tight text-ink-900">
            Connect your AI agent to Hey Tasky
          </h1>
          <p className="mt-2 max-w-full break-words text-ink-500">
            Hey Tasky runs a remote MCP server, so any MCP-capable agent (Claude, ChatGPT, Cursor,
            and more) can draft, schedule, and publish to your connected networks. You connect once,
            then Hey Tasky handles the per-network tokens, timing, and delivery.
          </p>
          <div className="mt-3 flex max-w-full flex-col gap-1 rounded-xl border border-line bg-panel px-3 py-2 sm:inline-flex sm:flex-row sm:items-center sm:gap-2">
            <span className="text-xs text-ink-400">MCP server URL</span>
            <code className="break-all font-mono text-sm text-ink-800">{MCP_URL}</code>
          </div>
        </div>

        <Card>
          <Heading
            icon={<ShieldCheck className="h-4 w-4" />}
            title="Two ways to connect"
            subtitle="Pick one. Most people want OAuth."
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-line p-4">
              <p className="text-sm font-semibold text-ink-900">OAuth (recommended)</p>
              <p className="mt-1 text-sm text-ink-500">
                Your agent opens a browser, you sign in to Hey Tasky and approve once. Nothing secret
                to copy or paste. Best for Claude, ChatGPT, Cursor, and other interactive clients.
              </p>
            </div>
            <div className="rounded-xl border border-line p-4">
              <p className="text-sm font-semibold text-ink-900">API key</p>
              <p className="mt-1 text-sm text-ink-500">
                A long-lived key for headless tools (n8n, Make, Zapier) or clients that cannot run a
                browser sign-in. Create and revoke keys under Settings, Developers.
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <Heading
            icon={<Plug className="h-4 w-4" />}
            title="Set up your client"
            subtitle="Choose your agent and the auth method."
          />

          <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex w-full flex-wrap gap-1 rounded-xl border border-line bg-panel p-1 sm:inline-flex sm:w-auto">
              {CLIENTS.map((c) => (
                <button
                  key={c.name}
                  type="button"
                  onClick={() => setClient(c)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                    client.name === c.name
                      ? 'border border-line bg-surface text-ink-900 shadow-card'
                      : 'text-ink-500 hover:text-ink-900'
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
            <div className="inline-flex rounded-xl border border-line bg-panel p-1">
              {(['OAuth', 'API key'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                    mode === m
                      ? 'border border-line bg-surface text-ink-900 shadow-card'
                      : 'text-ink-500 hover:text-ink-900'
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          <p className="mb-3 text-sm text-ink-500">{client.blurb}</p>

          <ol className="mb-3 list-decimal space-y-1 pl-5 text-sm text-ink-600">
            {client.steps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>

          {client.kind === 'connector' ? (
            <div className="relative max-w-full overflow-hidden rounded-xl border border-line bg-panel">
              <Copyable value={snippet} />
              <div className="p-4 pr-24">
                <p className="text-xs text-ink-400">Paste this URL into ChatGPT</p>
                <code className="break-all font-mono text-sm text-ink-800">{snippet}</code>
              </div>
            </div>
          ) : (
            <Code>{snippet}</Code>
          )}

          {mode === 'API key' && client.kind !== 'connector' && (
            <p className="mt-2 text-xs text-ink-400">
              Replace YOUR_API_KEY with a key from Settings, Developers. Treat it like a password.
            </p>
          )}
        </Card>

        <Card>
          <Heading
            icon={<Wrench className="h-4 w-4" />}
            title="What your agent can do"
            subtitle="The tools exposed over MCP."
          />
          <ul className="divide-y divide-line rounded-xl border border-line">
            {TOOLS.map((t) => (
              <li key={t.name} className="flex flex-col gap-0.5 px-4 py-3 sm:flex-row sm:gap-3">
                <code className="font-mono text-sm text-ink-900 sm:w-40">{t.name}</code>
                <span className="text-sm text-ink-500">{t.desc}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card>
          <Heading
            icon={<KeyRound className="h-4 w-4" />}
            title="Security"
            subtitle="How access is protected."
          />
          <ul className="list-disc space-y-1 pl-5 text-sm text-ink-500">
            <li>Tokens are scoped to your workspace and validated on every call.</li>
            <li>OAuth tokens are bound to the Hey Tasky MCP server, so they cannot be replayed elsewhere.</li>
            <li>API keys are stored as hashes, shown once at creation, and revocable instantly.</li>
            <li>Revoke any key under Settings, Developers; revocation takes effect immediately.</li>
          </ul>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
            <Link to="/settings" className="inline-flex items-center gap-1 text-brand-500 hover:text-brand-600">
              Manage API keys <ExternalLink className="h-3.5 w-3.5" />
            </Link>
            <a
              href="https://modelcontextprotocol.io/docs/tutorials/security/authorization"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-brand-500 hover:text-brand-600"
            >
              MCP authorization spec <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </Card>
      </main>
    </div>
  )
}
